import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowDown } from 'lucide-react';
import { PurchasePopup } from '../components/PurchasePopup';
import { TestimonialCard } from '../components/TestimonialCard';
import { BonusSection } from '../components/BonusSection';
import { PurchaseSection } from '../components/PurchaseSection';
export function SalesPage() {
  const scrollToPurchase = () => {
    const element = document.getElementById('purchase');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const testimonials = [{
    name: 'María',
    age: 32,
    city: 'Buenos Aires',
    text: 'Siempre me sentía culpable después de decir que no. Este libro me ayudó a entender que poner límites no me hace mala persona.'
  }, {
    name: 'Carolina',
    age: 29,
    city: 'Ciudad de México',
    text: 'Me agotaba tratando de complacer a todos. Ahora puedo priorizarme sin sentir ese nudo en el estómago.'
  }, {
    name: 'Sofía',
    age: 38,
    city: 'Bogotá',
    text: 'Las herramientas prácticas me sirvieron muchísimo. No es solo teoría, son ejercicios que podés aplicar ya.'
  }, {
    name: 'Valentina',
    age: 35,
    city: 'Santiago',
    text: 'Increíble cómo cambió mi relación con mi familia. Pude poner frenos necesarios desde el amor y la calma.'
  }, {
    name: 'Andrea',
    age: 41,
    city: 'Lima',
    text: 'El ritual de cierre nocturno es mi parte favorita. Dejé de llevarme las discusiones a la almohada.'
  }];
  return <div className="min-h-screen bg-purple-50 font-sans text-gray-800 overflow-x-hidden" data-id="element-55">
      <PurchasePopup data-id="element-56" />

      {/* Hero Section */}
      <header className="relative bg-gradient-to-b from-purple-900 via-purple-800 to-purple-600 text-white pb-20 pt-10 px-4 overflow-hidden" data-id="element-57">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-20" data-id="element-58"></div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-59">
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} className="text-center md:text-left" data-id="element-60">
            <span className="inline-block bg-pink-500 text-white px-4 py-1 rounded-full text-sm font-bold mb-6 tracking-wide uppercase" data-id="element-61">
              Nuevo Método
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6" data-id="element-62">
              DECIR <span className="text-pink-300" data-id="element-63">NO</span> <br data-id="element-64" />
              SIN SENTIRTE <span className="text-cyan-300" data-id="element-65">MALA</span>
            </h1>
            <p className="text-xl md:text-2xl text-purple-100 mb-8 font-light" data-id="element-66">
              Cómo poner límites sin pelear, sin justificarte y sin cargar con
              culpa después.
            </p>
            <button onClick={scrollToPurchase} className="bg-gradient-to-r from-pink-500 to-orange-400 hover:from-pink-600 hover:to-orange-500 text-white font-bold py-4 px-8 rounded-full shadow-lg transform transition hover:scale-105 text-lg w-full md:w-auto" data-id="element-67">
              QUIERO DECIR NO SIN SENTIRME MALA
            </button>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative mx-auto" data-id="element-68">
            <div className="absolute inset-0 bg-pink-500 blur-3xl opacity-30 rounded-full" data-id="element-69"></div>
            <img src="https://cdn.magicpatterns.com/uploads/2sGZJsKLQ7q79KQ4PLzjkL/cover_p9.png" alt="Decir No Sin Sentirte Mala Cover" className="relative z-10 w-full max-w-md mx-auto drop-shadow-2xl transform hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={scrollToPurchase} data-id="element-70" />
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-purple-50 to-transparent" data-id="element-71"></div>
      </header>

      {/* The Problem Section */}
      <section className="py-16 px-4 max-w-3xl mx-auto" data-id="element-72">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="prose prose-lg prose-purple mx-auto" data-id="element-73">
          <h2 className="text-3xl font-bold text-center text-purple-900 mb-8" data-id="element-74">
            Hay algo que casi nadie dice en voz alta.
          </h2>

          <p className="text-lg leading-relaxed" data-id="element-75">
            No es que no sepamos decir que no. Es que decir no nos desordena por
            dentro.
          </p>
          <p className="text-lg leading-relaxed" data-id="element-76">
            Podés estar convencida. Podés haber pensado el límite. Podés saber
            que es lo correcto.
          </p>
          <p className="font-bold text-xl text-purple-800 mt-6" data-id="element-77">Y aun así…</p>

          <div className="bg-white p-6 rounded-xl shadow-md border-l-4 border-pink-400 my-8" data-id="element-78">
            <p className="italic text-gray-700 m-0" data-id="element-79">
              Después viene la culpa. La tensión en el pecho. La cabeza que
              repasa la conversación una y otra vez. La sensación de haber hecho
              algo mal… aunque sepas que no.
            </p>
          </div>

          <p className="text-lg" data-id="element-80">
            Si esto te pasa, quiero que lo sepas desde ahora:
          </p>

          <div className="flex flex-wrap gap-4 justify-center my-8" data-id="element-81">
            {['No sos débil', 'No sos exagerada', 'No sos demasiado sensible'].map((item, i) => <span key={i} className="bg-purple-100 text-purple-800 px-4 py-2 rounded-lg font-bold flex items-center gap-2" data-id="element-82">
                <Check className="w-5 h-5" data-id="element-83" /> {item}
              </span>)}
          </div>

          <p className="text-lg font-medium text-center" data-id="element-84">
            Lo que te pasa tiene lógica. Y, lo más importante:{' '}
            <span className="text-pink-600 font-bold" data-id="element-85">tiene solución.</span>
          </p>
        </motion.div>
      </section>

      {/* The Real Problem Section */}
      <section className="py-16 px-4 bg-white" data-id="element-86">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-87">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-88">
            <h2 className="text-3xl font-bold text-purple-900 mb-6" data-id="element-89">
              El verdadero problema con los límites
            </h2>
            <p className="text-gray-700 mb-4 text-lg" data-id="element-90">
              Te enseñaron a comunicarte mejor. A ser asertiva. A decir lo que
              sentís.
            </p>
            <p className="text-gray-700 mb-4 text-lg font-medium" data-id="element-91">
              Pero nadie te enseñó qué hacer con lo que se activa adentro cuando
              lo hacés.
            </p>
            <p className="text-gray-700 mb-4" data-id="element-92">
              Porque el cuerpo no negocia con argumentos. Reacciona desde
              memorias antiguas.
            </p>
            <p className="text-gray-700" data-id="element-93">
              Y mientras tu cuerpo crea que decir no pone en riesgo el vínculo,
              la culpa va a aparecer. No importa cuántos libros leas. No importa
              cuánta razón tengas.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="bg-purple-100 rounded-3xl p-8 relative overflow-hidden" data-id="element-94">
            <div className="absolute top-0 right-0 w-32 h-32 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob" data-id="element-95"></div>
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000" data-id="element-96"></div>

            <h3 className="text-2xl font-bold text-purple-900 mb-4 relative z-10" data-id="element-97">
              Acá es donde todo cambia
            </h3>
            <p className="text-gray-800 relative z-10 mb-6" data-id="element-98">
              <strong data-id="element-99">DECIR NO SIN SENTIRTE MALA</strong> no te enseña a
              confrontar. Te enseña a sentirte segura mientras ponés un límite.
            </p>
            <button onClick={scrollToPurchase} className="w-full bg-white text-purple-700 font-bold py-3 px-6 rounded-xl shadow-md hover:bg-purple-50 transition relative z-10" data-id="element-100">
              Quiero aprender a poner límites
            </button>
          </motion.div>
        </div>
      </section>

      {/* The Solution / Methodology */}
      <section className="py-16 px-4 bg-gradient-to-br from-purple-900 to-indigo-900 text-white" data-id="element-101">
        <div className="max-w-5xl mx-auto" data-id="element-102">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-103">
            Este entrenamiento trabaja en tres momentos clave
          </h2>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-104">
            {[{
            title: 'Antes del límite',
            desc: 'Para que el no salga desde calma, no desde tensión.'
          }, {
            title: 'Durante el límite',
            desc: 'Para que tu tono, cuerpo y palabras estén alineados.'
          }, {
            title: 'Después del límite',
            desc: 'Para que no te castigues internamente.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 hover:bg-white/20 transition" data-id="element-105">
                <div className="text-4xl font-bold text-pink-400 mb-4" data-id="element-106">
                  0{i + 1}
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-107">{item.title}</h3>
                <p className="text-purple-100" data-id="element-108">{item.desc}</p>
              </motion.div>)}
          </div>

          <div className="mt-16 text-center" data-id="element-109">
            <h3 className="text-2xl font-bold mb-6" data-id="element-110">Lo que empieza a pasar</h3>
            <p className="text-lg max-w-3xl mx-auto mb-8 text-purple-100" data-id="element-111">
              Decís no… y tu día sigue. No necesitás justificarte. No llevás la
              conversación a la cama. No repasás lo que dijiste mil veces. No te
              preguntás si fuiste mala.
            </p>
            <p className="text-xl font-bold text-pink-300 mb-8" data-id="element-112">
              Te sentís firme sin estar dura.
            </p>
            <button onClick={scrollToPurchase} className="bg-pink-500 hover:bg-pink-600 text-white font-bold py-4 px-10 rounded-full shadow-lg transition transform hover:scale-105" data-id="element-113">
              SI, ESTO ES LO QUE NECESITO
            </button>
          </div>
        </div>
      </section>

      {/* Is this for you? */}
      <section className="py-16 px-4 bg-purple-50" data-id="element-114">
        <div className="max-w-3xl mx-auto bg-white p-8 md:p-12 rounded-3xl shadow-xl" data-id="element-115">
          <h2 className="text-3xl font-bold text-center text-purple-900 mb-10" data-id="element-116">
            Esto es para vos si…
          </h2>

          <div className="space-y-4 mb-10" data-id="element-117">
            {['Te cuesta decir no', 'Te sentís culpable cuando lo hacés', 'Te explicás de más', 'Te agotás por sostener a todos', 'Querés límites claros sin conflicto interno'].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex items-center gap-4 p-3 bg-purple-50 rounded-lg" data-id="element-118">
                <div className="bg-purple-200 p-1 rounded-full" data-id="element-119">
                  <Check className="w-5 h-5 text-purple-700" data-id="element-120" />
                </div>
                <span className="text-lg text-gray-800 font-medium" data-id="element-121">
                  {item}
                </span>
              </motion.div>)}
          </div>

          <div className="text-center bg-blue-50 p-6 rounded-xl border border-blue-100" data-id="element-122">
            <p className="text-gray-800 italic" data-id="element-123">
              "No es magia. Es regulación. El problema nunca fue que no supieras
              poner límites. El problema es que tu sistema nervioso aprendió que
              decir no era peligroso."
            </p>
          </div>
        </div>
      </section>

      {/* What is included */}
      <section className="py-16 px-4" data-id="element-124">
        <div className="max-w-4xl mx-auto text-center" data-id="element-125">
          <h2 className="text-3xl md:text-4xl font-bold text-purple-900 mb-6" data-id="element-126">
            ¿Qué es DECIR NO SIN SENTIRTE MALA?
          </h2>
          <p className="text-xl text-gray-600 mb-12" data-id="element-127">
            Un entrenamiento práctico, emocional y corporal.
          </p>

          <div className="grid md:grid-cols-2 gap-8 items-center mb-16" data-id="element-128">
            <div className="order-2 md:order-1 text-left space-y-6" data-id="element-129">
              <h3 className="text-2xl font-bold text-purple-800 mb-4" data-id="element-130">
                Incluye:
              </h3>
              <ul className="space-y-4" data-id="element-131">
                {['Guía paso a paso', 'Reencuadres internos claros', 'Ejercicios simples de regulación', 'Lenguaje realista (sin frases forzadas)', 'Protocolos para después del límite'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-132">
                    <div className="w-2 h-2 bg-pink-500 rounded-full" data-id="element-133"></div>
                    <span className="text-lg text-gray-700" data-id="element-134">{item}</span>
                  </li>)}
              </ul>
              <p className="font-bold text-purple-700 mt-6 pt-6 border-t border-purple-100" data-id="element-135">
                No te cambia la personalidad. Te devuelve estabilidad interna.
              </p>
            </div>
            <div className="order-1 md:order-2" data-id="element-136">
              <img src="https://cdn.magicpatterns.com/uploads/2sGZJsKLQ7q79KQ4PLzjkL/cover_p9.png" alt="Product Preview" className="w-full max-w-sm mx-auto rounded-lg shadow-2xl transform rotate-3 hover:rotate-0 transition duration-500" data-id="element-137" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-purple-50" data-id="element-138">
        <div className="max-w-6xl mx-auto" data-id="element-139">
          <h2 className="text-3xl font-bold text-center text-purple-900 mb-12" data-id="element-140">
            Lo que dicen quienes ya lo leyeron
          </h2>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-141">
            {testimonials.slice(0, 3).map((t, i) => <TestimonialCard key={i} {...t} delay={i * 0.1} data-id="element-142" />)}
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-6 max-w-4xl mx-auto" data-id="element-143">
            {testimonials.slice(3, 5).map((t, i) => <TestimonialCard key={i + 3} {...t} delay={i * 0.1} data-id="element-144" />)}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="bg-purple-900/5" data-id="element-145">
        <BonusSection data-id="element-146" />
      </section>

      {/* Final CTA / Purchase */}
      <PurchaseSection data-id="element-147" />

      {/* Footer */}
      <footer className="bg-purple-900 text-white py-12 px-4 text-center" data-id="element-148">
        <div className="max-w-3xl mx-auto" data-id="element-149">
          <h2 className="text-2xl font-bold mb-6" data-id="element-150">Empezá hoy</h2>
          <p className="text-purple-200 mb-8 text-lg" data-id="element-151">
            No tenés que volverte más dura. No tenés que explicar mejor. No
            tenés que convencer a nadie.
            <br data-id="element-152" />
            Solo necesitás que tu cuerpo deje de vivir el límite como una
            amenaza.
          </p>
          <button onClick={scrollToPurchase} className="bg-white text-purple-900 font-bold py-3 px-8 rounded-full shadow-lg hover:bg-gray-100 transition mb-8" data-id="element-153">
            Accedé ahora a DECIR NO SIN SENTIRTE MALA
          </button>
          <p className="text-sm text-purple-400" data-id="element-154">
            © {new Date().getFullYear()} Decir No Sin Sentirte Mala. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}